import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Send, User, ArrowLeft, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useConversations } from '../../hooks/useConversations';
import { PageLayout } from '../../components/layout/PageLayout';
import { PrivateMessage } from '../../types/messages';
import { Avatar } from '../../components/common/Avatar';
import { setActiveConversation } from '../../lib/firebase';
import { useTranslation } from '../../hooks/useTranslation';
import { AdSense } from '../../components/ads/AdSense';

interface Profile {
  id: string;
  username: string;
  avatar_url?: string;
}

interface RecipientProfile {
  username: string;
  fcm_token?: string;
}

interface Participant {
  user_id: string;
  profile: Profile;
}

interface Message {
  id: string;
  content: string;
  created_at: string;
  sender_id: string;
  user_id: string;
  sender: Profile;
}

export const ConversationDetail = ({ conversationId }: { conversationId: string }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { fetchConversations } = useConversations();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [otherUser, setOtherUser] = useState<Profile | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);
  const { t, getPathForLang } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!loading && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView();
    }
  }, [loading]);

  useEffect(() => {
    if (!conversationId || !user) return;

    setLoading(true);

    const fetchInitialData = async () => {
      try {
        // Vérifier d'abord si l'utilisateur est un participant
        const { data: participant, error: participantError } = await supabase
          .from('conversation_participants')
          .select('*')
          .eq('conversation_id', conversationId)
          .eq('user_id', user.id)
          .single();

        if (participantError || !participant) {
          console.error('User is not a participant of this conversation');
          navigate('/messages');
          return;
        }

        const [messagesResponse, conversationResponse] = await Promise.all([
          supabase
            .from('private_messages')
            .select(`
              *,
              sender:profiles!user_id(
                id,
                username,
                avatar_url
              )
            `)
            .eq('conversation_id', conversationId)
            .order('created_at', { ascending: true }),

          supabase
            .from('conversations')
            .select(`
              *,
              participants:conversation_participants (
                user_id,
                profile:profiles (
                  username,
                  avatar_url
                )
              )
            `)
            .eq('id', conversationId)
            .single()
        ]);

        if (messagesResponse.error) throw messagesResponse.error;
        if (conversationResponse.error) throw conversationResponse.error;

        setMessages(messagesResponse.data || []);

        const otherParticipant = conversationResponse.data.participants.find(
          (p: Participant) => p.user_id !== user.id
        );
        setOtherUser(otherParticipant?.profile);

        await supabase.rpc('reset_unread_count', {
          p_conversation_id: conversationId,
          p_user_id: user.id
        });

        // Réinitialiser le compteur de messages non lus
        await supabase
          .from('conversation_participants')
          .update({ unread_count: 0 })
          .eq('conversation_id', conversationId)
          .eq('user_id', user.id);

        // Rafraîchir la liste des conversations pour mettre à jour les compteurs
        fetchConversations();

      } catch (error) {
        console.error('Error fetching initial data:', error);
        navigate('/messages');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();

    const channel = supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'private_messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        async (payload) => {
          console.log('Nouveau message détecté:', payload);
          
          // Vérifier si le message est déjà dans notre liste
          const messageExists = messages.some(msg => msg.id === payload.new.id);
          if (messageExists) {
            console.log('Message déjà présent dans la liste, ignoré');
            return;
          }
          
          const { data: messageWithSender, error } = await supabase
            .from('private_messages')
            .select(`
              *,
              sender:profiles!user_id(
                id,
                username,
                avatar_url
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (error) {
            console.error('Erreur lors de la récupération du message avec expéditeur:', error);
            return;
          }

          if (messageWithSender) {
            console.log('Ajout du nouveau message à la liste:', messageWithSender);
            setMessages(prev => [...prev, messageWithSender]);
            
            // Réinitialiser le compteur de messages non lus
            if (messageWithSender.user_id !== user?.id) {
              await supabase
                .from('conversation_participants')
                .update({ unread_count: 0 })
                .eq('conversation_id', conversationId)
                .eq('user_id', user?.id);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, user, navigate]);

  useEffect(() => {
    if (!user) return;

    const fetchProfileId = async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        setCurrentProfileId(profile.id);
      }
    };

    fetchProfileId();
  }, [user]);

  useEffect(() => {
    if (!conversationId || !user) return;

    const resetUnreadCount = async () => {
      try {
        // Réinitialiser le compteur de messages non lus
        const { error } = await supabase
          .from('conversation_participants')
          .update({ unread_count: 0 })
          .eq('conversation_id', conversationId)
          .eq('user_id', user.id);

        if (error) {
          console.error('Error resetting unread count:', error);
        }
      } catch (error) {
        console.error('Error in resetUnreadCount:', error);
      }
    };

    resetUnreadCount();
  }, [conversationId, user]);

  // Mettre à jour la présence de l'utilisateur
  useEffect(() => {
    if (!user || !conversationId) return;

    // Mettre à jour la présence immédiatement
    const updatePresence = async () => {
      await supabase
        .from('presence')
        .upsert({
          user_id: user.id,
          conversation_id: conversationId,
          last_seen: new Date().toISOString()
        }, {
          onConflict: 'user_id,conversation_id'
        });
    };

    // Mettre à jour la présence toutes les 15 secondes
    updatePresence();
    const interval = setInterval(updatePresence, 15000);

    // Nettoyer l'intervalle quand l'utilisateur quitte la conversation
    return () => {
      clearInterval(interval);
    };
  }, [user, conversationId]);

  // Ajouter cet effet pour gérer les notifications
  useEffect(() => {
    if (!user || !conversationId) return;

    // Supprimer les notifications non lues pour cette conversation
    const cleanupNotifications = async () => {
      console.log('Nettoyage des notifications pour:', {
        user_id: user.id,
        conversation_id: conversationId
      });

      // D'abord marquer comme lues
      const { error: updateError } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('conversation_id', conversationId)
        .eq('type', 'new_message')
        .eq('read', false);

      if (updateError) {
        console.error('Erreur lors de la mise à jour des notifications:', updateError);
        return;
      }

      // Puis supprimer
      const { error: deleteError } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id)
        .eq('conversation_id', conversationId)
        .eq('type', 'new_message');

      if (deleteError) {
        console.error('Erreur lors du nettoyage des notifications:', deleteError);
      } else {
        console.log('Notifications nettoyées avec succès');
      }
    };

    // Nettoyer les notifications à l'ouverture de la conversation
    cleanupNotifications();

    // Écouter les nouvelles notifications
    const channel = supabase
      .channel(`notifications-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Nouvelle notification détectée:', payload);
          if (payload.new.conversation_id === conversationId) {
            console.log('Notification pour la conversation courante, nettoyage...');
            cleanupNotifications();
          }
        }
      )
      .subscribe();

    return () => {
      console.log('Nettoyage de la souscription aux notifications');
      supabase.removeChannel(channel);
    };
  }, [user, conversationId]);

  useEffect(() => {
    // Définir la conversation active
    setActiveConversation(conversationId);

    // Nettoyer quand on quitte la conversation
    return () => {
      setActiveConversation(null);
    };
  }, [conversationId]);

  const sendMessage = async (content: string) => {
    try {
      // Récupérer le destinataire
      const { data: recipient } = await supabase
        .from('conversation_participants')
        .select(`
          user_id,
          profile:profiles!conversation_participants_user_id_fkey (
            username,
            fcm_token
          )
        `)
        .eq('conversation_id', conversationId)
        .neq('user_id', user?.id)
        .single();

      // Type assertion pour le recipient
      const typedRecipient: { 
        user_id: string; 
        profile: RecipientProfile 
      } = recipient as any;

      // Envoyer le message
      const messageData = {
        conversation_id: conversationId,
        user_id: user?.id,
        content: content
      };

      const { data: newMessage, error: messageError } = await supabase
        .from('private_messages')
        .insert(messageData)
        .select()
        .single();

      if (messageError) throw messageError;

      // Créer la notification
      if (typedRecipient) {
        // Récupérer le nom de l'expéditeur
        const { data: sender } = await supabase
          .from('profiles')
          .select('username')
          .eq('user_id', user?.id)
          .single();

        // Préparer le contenu du message pour la notification
        const notificationContent = content.length > 50 
          ? content.substring(0, 47) + '...' 
          : content;

        await supabase
          .from('notifications')
          .insert({
            user_id: typedRecipient.user_id,
            title: `Message de ${sender?.username}`,
            body: notificationContent,
            type: 'new_message',
            conversation_id: conversationId,
            recipient_token: typedRecipient.profile?.fcm_token,
            read: false
          });
      }
    } catch (error) {
      console.error('Error in sendMessage:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const messageContent = newMessage.trim();
    if (!messageContent || !conversationId || !user) return;

    // Vider l'input immédiatement
    setNewMessage('');

    try {
      await sendMessage(messageContent);
    } catch (error) {
      console.error('Error sending message:', error);
      // En cas d'erreur, restaurer le message
      setNewMessage(messageContent);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return t('messages.conversation.today');
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t('messages.conversation.yesterday');
    } else {
      return date.toLocaleDateString();
    }
  };

  // Détecter si l'appareil est mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (loading) {
    return (
      <PageLayout>
        <div className="max-w-4xl mx-auto px-4 py-8 animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-8" />
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-10 h-10 bg-gray-200 rounded-full" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 md:p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-3">
          <button 
            onClick={() => navigate(getPathForLang('/messages'))}
            className="md:hidden text-gray-600 hover:text-gray-900 transition-colors"
            aria-label={t('messages.conversation.back_to_list_aria')}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          {otherUser && (
            <div className="flex items-center gap-2 md:gap-3">
              <div className="relative group">
                <Avatar 
                  url={otherUser.avatar_url} 
                  username={otherUser.username}
                  size="md"
                />
                <Link
                  to={getPathForLang(`/users/${otherUser.username}`)}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100"
                  title={t('messages.conversation.view_profile')}
                  aria-label={t('messages.conversation.view_profile_aria')}
                >
                  <Users className="w-5 h-5 text-white" />
                </Link>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{otherUser.username}</h2>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Bannière publicitaire uniquement sur desktop */}
      {!isMobile && (
        <div className="w-full py-1 border-b border-gray-200 bg-white flex justify-center items-center">
          <div className="max-w-[728px] h-[90px] flex justify-center items-center">
            <AdSense 
              adSlot="1424794127"
              id="messages-desktop-banner"
              adFormat="horizontal"
              style={{ 
                display: 'block',
                width: '728px',
                height: '90px'
              }}
            />
          </div>
        </div>
      )}
      
      <div 
        className="flex-1 overflow-y-auto p-4 space-y-4"
        ref={messagesContainerRef}
      >
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex justify-center items-center h-full text-gray-500">
            {t('messages.no_message')}
          </div>
        ) : (
          <>
            {messages.reduce((acc: React.ReactNode[], message, index) => {
              const messageDate = formatDate(message.created_at);
              
              if (index === 0 || formatDate(messages[index - 1].created_at) !== messageDate) {
                acc.push(
                  <div key={`date-${messageDate}`} className="flex justify-center my-4">
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-600">
                      {messageDate}
                    </span>
                  </div>
                );
              }
              
              const isCurrentUser = message.user_id === user?.id;
              
              acc.push(
                <div 
                  key={message.id} 
                  className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div className="flex items-end gap-2 max-w-[75%]">
                    {!isCurrentUser && (
                      <Avatar 
                        url={message.sender.avatar_url} 
                        username={message.sender.username}
                        size="sm"
                      />
                    )}
                    <div 
                      className={`py-2 px-3 rounded-lg ${
                        isCurrentUser 
                          ? 'bg-primary-100 text-primary-900' 
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                      <div className="text-[10px] text-right mt-1 opacity-70">
                        {new Date(message.created_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
              
              return acc;
            }, [])}
            
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
      
      {otherUser && (
        <div className="p-3 md:p-4 border-t border-gray-200">
          <form onSubmit={handleSubmit} className="flex items-end gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={messageInputRef}
                value={newMessage}
                onChange={handleMessageChange}
                onKeyDown={handleKeyDown}
                placeholder={t('messages.conversation.type_message')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm"
                rows={1}
                style={{ minHeight: '40px', maxHeight: '120px' }}
              />
            </div>
            <button
              type="submit"
              disabled={!newMessage.trim()}
              className="flex-shrink-0 bg-primary-500 text-white p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-600 transition-colors"
              aria-label={t('messages.conversation.send_aria')}
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}; 